import React from 'react';
import styled from 'styled-components';
import { usePublicSettings } from '../contexts/PublicSettingsContext';
import { apiService, GalleryItem } from '../services/ApiService';
import { transformImageUrlForEnvironment } from '../../utils/imageUrlUtils';

interface DynamicButtonProps {
    href: string;
    target?: string;
    rel?: string;
    children: React.ReactNode;
    buttonType: 'etsy' | 'instagram';
    className?: string;
}

const StyledButton = styled.a`
    display: inline-block;
    border-radius: 3px;
    background: transparent;
    color: black;
    border: 2px solid black;
    box-sizing: border-box;
    -moz-box-sizing: border-box;
    -webkit-box-sizing: border-box;
    padding-top: 8px;
    padding-bottom: 8px;
    margin-top: 8px;
    margin-bottom: 8px;
    font-family: "Optima", sans-serif;
    font-style: normal;
    font-weight: 200;
    font-size: 2em;
    text-align: center;
    width: 100%;
    text-decoration: none;
    transition: all 0.2s ease;

    &:hover {
        background: #f8f9fa;
        transform: translateY(-1px);
    }

    &:visited {
        color: black;
    }
`;

const ImageButton = styled.a`
    display: block;
    margin-top: 8px;
    margin-bottom: 8px;
    width: 100%;
    text-decoration: none;
    transition: all 0.2s ease;
    border-radius: 3px;
    border: 2px solid black;
    box-sizing: border-box;
    -moz-box-sizing: border-box;
    -webkit-box-sizing: border-box;
    overflow: hidden; /* Hide any overflow from the image */

    &:hover {
        transform: translateY(-1px);
        opacity: 0.9;
    }
`;

const ButtonImage = styled.img`
    display: block;
    width: 100%;
    height: 60px; /* Base height for small screens */
    object-fit: cover; /* Fill the container, cropping if necessary */
    border-radius: 3px;
    box-sizing: border-box;
    -moz-box-sizing: border-box;
    -webkit-box-sizing: border-box;

    /* Responsive height for larger screens */
    @media (min-width: 768px) {
        height: 80px; /* Taller on tablets and up */
    }

    @media (min-width: 1024px) {
        height: 100px; /* Even taller on desktop screens */
    }

    @media (min-width: 1440px) {
        height: 120px; /* Tallest on very large screens */
    }
`;

const LoadingSpinner = styled.div`
    display: inline-block;
    width: 20px;
    height: 20px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #007bff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-right: 8px;
    
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;

export const DynamicButton: React.FC<DynamicButtonProps> = ({ 
    href, 
    target, 
    rel, 
    children, 
    buttonType,
    className 
}) => {
    const { settings } = usePublicSettings();
    const [buttonImage, setButtonImage] = React.useState<GalleryItem | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const imageId = buttonType === 'etsy' 
        ? settings.etsyButtonImageId 
        : settings.instagramButtonImageId;

    // Prefer direct URL from snapshots when available (avoids API fetch)
    const directUrl = buttonType === 'etsy'
        ? settings.etsyButtonImageUrl
        : settings.instagramButtonImageUrl;

    React.useEffect(() => {
        // If we have a direct URL from snapshots, skip fetching by ID
        if (directUrl) {
            setButtonImage(null);
            setLoading(false);
            setError(null);
            return;
        }

        if (imageId) {
            loadButtonImage(imageId);
        } else {
            setButtonImage(null);
        }
    }, [imageId, directUrl]);

    const loadButtonImage = async (id: string) => {
        setLoading(true);
        setError(null);
        try {
            // Use public endpoint for individual gallery items (including private button images)
            const response = await apiService.getPublicGalleryItem(id);
            setButtonImage(response.item);
        } catch (err) {
            console.error(`Error loading ${buttonType} button image:`, err);
            setError('Failed to load button image');
            setButtonImage(null);
        } finally {
            setLoading(false);
        }
    };

    const handleClick = () => {
        // Track click before navigation
        apiService.trackExternalClick(buttonType);
        // Let the default navigation happen
    };

    // If loading, show text button with spinner
    if (loading) {
        return (
            <StyledButton href={href} target={target} rel={rel} className={className} onClick={handleClick}>
                <LoadingSpinner />
                {children}
            </StyledButton>
        );
    }

    // If we have a direct URL, render image button using it
    if (directUrl) {
        return (
            <ImageButton href={href} target={target} rel={rel} className={className} onClick={handleClick}>
                <ButtonImage 
                    src={transformImageUrlForEnvironment(directUrl)} 
                    alt={children ? children.toString() : 'Button'}
                />
            </ImageButton>
        );
    }

    // If error or no image configured, show text button
    if (error || !buttonImage) {
        return (
            <StyledButton href={href} target={target} rel={rel} className={className} onClick={handleClick}>
                {children}
            </StyledButton>
        );
    }

    // Show image button
    return (
        <ImageButton href={href} target={target} rel={rel} className={className} onClick={handleClick}>
            <ButtonImage 
                src={transformImageUrlForEnvironment(buttonImage.full_image_url)} 
                alt={buttonImage.button_text || (children ? children.toString() : 'Button')}
            />
        </ImageButton>
    );
};
